import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { nullish, object, string } from "valibot";
import { dataName, label, rememberInput, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { TEXT_CONDITION_KEYS } from "../../constants";
import FieldValueReference from "../FieldValueReference";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import SimpleTable from "../../components/table/SimpleTable";
import DetailModal from "../../components/modal/DetailModal";
import FormatDate from "../../components/formatting/FormatDate";
import { at } from "lodash-es";
import ButtonLink from "../../components/link/ButtonLink";
import { getDynamicValueIcon } from "../../utils/menu/optionIconUtil";
import { useModalManager } from "../../components/modal/ModalManager";
import { Show } from "solid-js";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector, t } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  required: required(),
  remember_input: rememberInput(),
});
const ValueSchema = object({
  licensePlate: string(),
  type: nullish(string()),
  brand: nullish(string()),
  apkExpirationDate: nullish(string()),
  euroClassification: nullish(string()),
  firstInternationalAdmission: nullish(string()),
});

export type LicensePlateFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const LicensePlateField: Field<LicensePlateFieldProperties, DefaultDtoType, ValueType> = {
  type: "licensePlate",
  category: "searchItems",
  widget: "com.moreapps:rdw:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => {
    const { t } = useTranslation();
    return {
      label_text: t(($) => $.LICENSE_PLATE_FIELD_LABEL_TEXT),
      required: false,
      remember_input: false,
    };
  },
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "text" },
    actions: { variant: "text" },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value, path = ["licensePlate"]) => at(value, path.join("."))[0] ?? "",
  renderValue: (value, { fieldConfig }) => {
    const { t } = useTranslation();
    const { open } = useModalManager();

    return (
      <ButtonLink
        label={value.licensePlate}
        onClick={() => {
          open({
            content: () => (
              <DetailModal
                title={fieldConfig.properties.label_text}
                iconTile={{ icon: LicensePlateField.icon, category: LicensePlateField.category }}
                content={() => (
                  <SimpleTable
                    data={[
                      { label: t(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_TYPE), value: value.type },
                      { label: t(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_BRAND), value: value.brand },
                      { label: t(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_LICENSE_PLATE), value: value.licensePlate },
                      {
                        label: t(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_APK_EXPIRATION_DATE),
                        value: (
                          <Show when={value.apkExpirationDate}>
                            {(v) => <FormatDate value={new Date(v())} dateOnly />}
                          </Show>
                        ),
                      },
                      {
                        label: t(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_FIRST_INTERNATIONAL_ADMISSION),
                        value: (
                          <Show when={value.firstInternationalAdmission}>
                            {(v) => <FormatDate value={new Date(v())} dateOnly />}
                          </Show>
                        ),
                      },
                    ]}
                  />
                )}
              />
            ),
          });
        }}
      />
    );
  },
  icon: "truck",
  titleKey: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  valueReferenceOptions: (id, props) =>
    [
      { key: ["licensePlate"], label: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_LICENSE_PLATE) },
      { key: ["brand"], label: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_BRAND) },
      { key: ["type"], label: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_TYPE) },
      {
        key: ["apkExpirationDate"],
        label: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_APK_EXPIRATION_DATE),
      },
      {
        key: ["firstInternationalAdmission"],
        label: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_FIRST_INTERNATIONAL_ADMISSION),
      },
      {
        key: ["euroClassification"],
        label: keyFromSelector(($) => $.LICENSE_PLATE_FIELD_SUB_VALUE_EURO_CLASSIFICATION),
      },
    ].map(({ key, label }) => {
      const referenceId = new FieldValueReference(id, key);
      const icon = getDynamicValueIcon(id, LicensePlateField.type);
      return {
        variant: "dynamicValue",
        value: referenceId,
        label: t(label),
        pill: props.data_name,
        icon,
      };
    }),
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <InputPreview leftIcon="car" placeholder={t(($) => $.LICENSE_PLATE_FIELD_PREVIEW_PLACEHOLDER)} />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(LicensePlateField);
